import { useFpB2bButlerItemsService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-butler-items-service'
import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { type ButlerItem } from '@cart-services/types'
import { ErrorFilled, SuccessFilled } from '@pets-ui/icons'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as Stylex from '@stylexjs/stylex'
import { ButlerListItem } from '../ButlerListItem/ButlerListItem'
import { type ButlerListItemProps } from '../ButlerListItem/ButlerListItem'
import { useButler } from '../ButlerProvider/useButler'

const styles = Stylex.create({
  snackber: {
    backgroundColor: colors.grey800,
    color: colors.white,
    minWidth: '320px',
    width: '100%',
  },
})

export const ButlerListItems = () => {
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { projectCode, countryCode, campaignId, campaignCode, platform } = useButler()
  const { orderId, items: butlerItems, upsertButlerItem } = useFpB2bButlerItemsService(campaignId, platform)
  const { productMap } = useFpB2bProductsInfo({
    projectCode,
    countryCode,
    campaignId,
    campaignCode,
    extraProductIds: butlerItems.map(item => item.variation_id).filter(variationId => variationId !== undefined),
    platform,
  })
  const handleButlerItemQuantityChange = (butlerItem: ButlerItem) => (value?: number) => {
    if (value === undefined || value === 0) return
    upsertButlerItem({
      cartItemId: butlerItem.cartItemId,
      quantity: value,
    })
  }
  const handleButlerItemCheckboxChange = (butlerItem: ButlerItem): ButlerListItemProps['onCheckboxChange'] => (event) => {
    upsertButlerItem({
      cartItemId: butlerItem.cartItemId,
      checked: event.target.checked,
    })
  }
  const handleButlerItemDelete = (butlerItem: ButlerItem): ButlerListItemProps['onDelete'] => () => {
    upsertButlerItem({
      cartItemId: butlerItem.cartItemId,
      isDeleted: true,
    })
  }
  const handleButlerItemRecover = (butlerItem: ButlerItem): ButlerListItemProps['onDeleteRecover'] => () => {
    upsertButlerItem({
      cartItemId: butlerItem.cartItemId,
      isDeleted: false,
    })
  }

  const handleExchangeButlerItem = (butlerItem: ButlerItem): ButlerListItemProps['onExchangeButlerItem'] => (exchangeProduct) => {
    try {
      if (exchangeProduct.parent_product_id === undefined || exchangeProduct.variation_id === undefined || exchangeProduct.sku === undefined) {
        throw new Error(`Invalid selected exchange product=${JSON.stringify(exchangeProduct)}`)
      }

      upsertButlerItem({
        cartItemId: butlerItem.cartItemId,
        variation_id: exchangeProduct.variation_id,
        parent_product_id: exchangeProduct.parent_product_id,
        sku: exchangeProduct.sku,
        quantity: exchangeProduct.quantity ?? butlerItem.quantity,
        stock: exchangeProduct.stock,
      })

      openSnackbar({
        xStyles: { snackbar: styles.snackber },
        message: '商品更換成功',
        icon: <SuccessFilled color={colors.green600} />,
      })
    }
    catch (error) {
      openSnackbar({
        xStyles: { snackbar: styles.snackber },
        message: '商品更換失敗，請再試一次！',
        icon: <ErrorFilled color={colors.red600} />,
      })
      console.error('Exchange butler item failed:', error)
    }
  }

  return (
    <>
      {butlerItems?.map((item) => {
        const product = item.variation_id ? productMap?.[item.variation_id] : undefined
        return (
          <ButlerListItem
            key={item.cartItemId}
            item={item}
            product={product}
            orderId={orderId}
            projectCode={projectCode}
            countryCode={countryCode}
            campaignCode={campaignCode}
            campaignId={campaignId}
            productTitle={product?.title}
            productImgSrc={product?.thumbnail ?? undefined}
            salePrice={product?.sale_price}
            onQuantityChange={handleButlerItemQuantityChange(item)}
            onCheckboxChange={handleButlerItemCheckboxChange(item)}
            onDelete={handleButlerItemDelete(item)}
            onDeleteRecover={handleButlerItemRecover(item)}
            onExchangeButlerItem={handleExchangeButlerItem(item)}
          />
        )
      })}
    </>
  )
}
